// State + orchestration for the document file cache (mode, progress, stats).
import { openOfflineDb } from "./db";
import { runFullSync, ensurePersisted, cacheStats, evictDocs, deleteFileCache } from "./fileCache";
import { MODES, resolveFileSyncMode } from "./filePlan";
import { subscribeEvents as subscribeSyncEvents } from "./syncManager";

const modeKey = (t) => `od.fileSyncMode.${t}`;


let state = {
  tenant: null,
  mode: "none",
  inFlight: false,
  progress: null,      // {total, done, stored, pending, failed, deleted, bytes, current}
  lastRun: null,
  lastResult: null,    // ok | auth | aborted | error
  cachedCount: 0,
  cachedBytes: 0,
  cachedKeys: new Set(),
};
const listeners = new Set();
let inflight = null;
let controller = null;
let eventsHooked = false;

function emit(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => { try { fn(state); } catch { /* ignore */ } });
}
export function getFileSyncState() { return state; }
export function subscribe(fn) { listeners.add(fn); fn(state); return () => listeners.delete(fn); }

const ls = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del(k) { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

export async function refreshFileStats(tenant = state.tenant) {
  const db = openOfflineDb(tenant);
  if (!db) return;
  try {
    const s = await cacheStats(db);
    emit({ cachedCount: s.count, cachedBytes: s.bytes, cachedKeys: s.keys });
  } catch { /* ignore */ }
}

export async function initFileSync(tenant) {
  if (!tenant) return;
  const mode = resolveFileSyncMode(ls.get(modeKey(tenant)));
  emit({ tenant, mode });
  await refreshFileStats(tenant);
  if (!eventsHooked) {
    eventsHooked = true;
    subscribeSyncEvents((evt) => {
      const t = state.tenant;
      if (!t) return;
      if (evt.type === "docs-removed") {
        evictDocs(t, openOfflineDb(t), evt.docIds).then(() => refreshFileStats(t)).catch(() => {});
      } else if (evt.type === "done" && state.mode !== "none") {
        startFileSync(t, { reason: "data-sync" }).catch(() => {});
      }
    });
  }
}

export function setFileSyncMode(tenant, mode) {
  if (!MODES.includes(mode)) return Promise.resolve(null);
  ls.set(modeKey(tenant), mode);
  emit({ mode });
  if (mode === "none") return Promise.resolve(null);
  return startFileSync(tenant, { reason: "mode" });
}

export function startFileSync(tenant, { reason = "manual" } = {}) {
  if (inflight) return inflight;
  controller = new AbortController();
  inflight = (async () => {
    const db = openOfflineDb(tenant);
    if (!db) return { status: "error" };
    await ensurePersisted();
    emit({ inFlight: true, progress: { total: 0, done: 0, stored: 0, pending: 0, failed: 0, deleted: 0, bytes: 0, current: null } });
    let lastEmit = 0;
    const result = await runFullSync({
      tenant, db, mode: state.mode, signal: controller.signal,
      onProgress: (p) => {
        const now = Date.now();
        if (now - lastEmit > 250 || p.done === p.total) { lastEmit = now; emit({ progress: p }); }
      },
    });
    await refreshFileStats(tenant);
    emit({ inFlight: false, lastRun: new Date().toISOString(), lastResult: result.status, progress: result, lastReason: reason });
    return result;
  })().finally(() => { inflight = null; controller = null; });
  return inflight;
}

export function abortFileSync() { controller?.abort(); }

export async function clearFileCache(tenant) {
  abortFileSync();
  await deleteFileCache(tenant, openOfflineDb(tenant));
  ls.set(modeKey(tenant), "none");
  ls.del(`od.fileSyncDialogDone.${tenant}`);  // legacy key of the removed dialog
  emit({ mode: "none", inFlight: false, progress: null, lastRun: null, lastResult: null, cachedCount: 0, cachedBytes: 0, cachedKeys: new Set() });
}
